import { useEffect, useRef } from "react";
import { InteractiveElementShell } from "@/components/interactive/InteractiveElementShell";
import QuizResultSummary from "@/components/storyboard/quiz/QuizResultSummary";
import WorksheetRunner, {
  type WorksheetRunnerState,
} from "@/components/storyboard/worksheet/WorksheetRunner";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useInteractiveElementState } from "@/hooks/useInteractiveElementState";
import { useValidationSubmissions } from "@/hooks/useValidationSubmissions";
import { resolveWorksheetStatus } from "@/lib/worksheetStatus";
import {
  getInteractiveElementState,
  setInteractiveElementState,
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import { isWorksheetAnswerRecord } from "@/lib/worksheetAnswers";
import type { WorksheetData } from "@/types/storyboard";
import type { ValidationResult } from "@/services/QuizValidationService";

// The answers of a completed worksheet, absent for legacy completions.
const worksheetReview = (status: InteractiveElementStatus) =>
  status.kind === InteractiveElementStatusKind.Completed &&
  isWorksheetAnswerRecord(status.review)
    ? status.review
    : undefined;

type CompletedWorksheetStatus = Extract<
  InteractiveElementStatus,
  { kind: InteractiveElementStatusKind.Completed }
>;

interface WorksheetCompletedViewProps {
  status: CompletedWorksheetStatus;
  data: WorksheetData;
  onValidate: (answer: string, taskIndex: number) => Promise<ValidationResult>;
}

/**
 * Completed-card body — full recap when answers were stored, else the compact
 * score pill used for legacy completions.
 */
const WorksheetCompletedView = ({
  status,
  data,
  onValidate,
}: WorksheetCompletedViewProps) => {
  const review = worksheetReview(status);
  if (!review) {
    return <QuizResultSummary score={status.score} compact />;
  }

  return (
    <WorksheetRunner
      data={data}
      onValidate={onValidate}
      readOnly
      reviewRecord={review}
    />
  );
};

export interface WorksheetInteractiveFlowProps {
  id: string;
  title: string;
  data: WorksheetData;
  onValidate: (answer: string, taskIndex: number) => Promise<ValidationResult>;
}

/**
 * Worksheet ("Arbeitsblatt") in the shared interactive-element shell — inline
 * in the storyboard card. After the last task, the read-only recap opens on
 * the card directly (no Fertig step).
 */
const WorksheetInteractiveFlow = ({
  id,
  title,
  data,
  onValidate,
}: WorksheetInteractiveFlowProps) => {
  const { enrollmentId } = useCourseDetailStore();
  const { submissions, refresh, isReady } = useValidationSubmissions(id);
  const { status: sessionStatus } = useInteractiveElementState(
    id,
    enrollmentId
  );
  // What the server knows decides the card on a fresh load. Once something has
  // happened in this session that state is fresher, so it takes over.
  const serverStatus = resolveWorksheetStatus(submissions, data);
  const statusOverride =
    sessionStatus.kind === InteractiveElementStatusKind.NotStarted
      ? serverStatus
      : undefined;

  // The status icon of the element reads the in-memory store, so a status the
  // server derived has to be shown there as well.
  const showServerStatusOnBadge = () => {
    if (!serverStatus || !enrollmentId) return;
    if (sessionStatus.kind !== InteractiveElementStatusKind.NotStarted) return;
    setInteractiveElementState(id, enrollmentId, serverStatus);
  };
  useEffect(showServerStatusOnBadge, [
    id,
    enrollmentId,
    serverStatus,
    sessionStatus.kind,
  ]);

  const partialRef = useRef<WorksheetRunnerState | null>(null);
  const indexRef = useRef<number>(0);
  // Prevents the close hook from overwriting a fresh "bearbeitet" with "angefangen".
  const completedRef = useRef<boolean>(false);

  // Keep a stable onPartialChange identity so the runner Effect that depends
  // on it does not re-fire when persist notifies a subscribed parent (badge /
  // shell) and re-renders this flow.
  const persistPartialRef = useRef<
    (partial: WorksheetRunnerState, index: number) => void
  >(() => undefined);

  const saveProgressSoLearnerCanResume = () => {
    persistPartialRef.current = (partial, index) => {
      partialRef.current = partial;
      indexRef.current = index;
      if (completedRef.current) return;
      const current = getInteractiveElementState(id, enrollmentId);
      if (current.kind === InteractiveElementStatusKind.Completed) return;
      const alreadyStored =
        current.kind === InteractiveElementStatusKind.InProgress &&
        current.lastTaskIndex === index &&
        JSON.stringify(current.partial) === JSON.stringify(partial);
      if (alreadyStored) return;
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial,
        lastTaskIndex: index,
      });
    };
  };
  useEffect(saveProgressSoLearnerCanResume);

  const persistInProgressDraft = useRef(
    (partial: WorksheetRunnerState, index: number) => {
      persistPartialRef.current(partial, index);
    }
  ).current;

  const handleBeforeClose = () => {
    if (completedRef.current) {
      completedRef.current = false;
      partialRef.current = null;
      indexRef.current = 0;
      return;
    }
    const current = getInteractiveElementState(id, enrollmentId);
    // Reviewing an already completed worksheet: leave it untouched.
    if (current.kind === InteractiveElementStatusKind.Completed) return;

    if (partialRef.current != null) {
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial: partialRef.current,
        lastTaskIndex: indexRef.current,
      });
    }
    partialRef.current = null;
    indexRef.current = 0;
  };

  return (
    <InteractiveElementShell
      contentElementId={id}
      enrollmentId={enrollmentId}
      title={title}
      illustrationKind="worksheet"
      layout="inline"
      supportsInProgress
      statusOverride={statusOverride}
      onBeforeClose={handleBeforeClose}
      completedView={(status) => (
        <WorksheetCompletedView
          status={status}
          data={data}
          onValidate={onValidate}
        />
      )}
      editingView={({ status, complete, close }) => {
        // The runner seeds from useState on first mount: wait until the saved
        // submissions are known so a resume does not start at the first task.
        if (!isReady) return null;

        const initialPartial =
          status.kind === InteractiveElementStatusKind.InProgress
            ? (status.partial as WorksheetRunnerState)
            : undefined;

        return (
          <WorksheetRunner
            data={data}
            onValidate={async (answer, taskIndex) => {
              const result = await onValidate(answer, taskIndex);
              refresh();
              return result;
            }}
            initialPartial={initialPartial}
            onPartialChange={persistInProgressDraft}
            onComplete={(score, record) => {
              completedRef.current = true;
              complete(score, record);
              close();
            }}
          />
        );
      }}
    />
  );
};

export default WorksheetInteractiveFlow;
